import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  LinearProgress,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import FilterListIcon from '@mui/icons-material/FilterList';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import RouterIcon from '@mui/icons-material/Router';
import { firewallService, FirewallGeraet, FirewallOverview, FirewallRegel, FirewallVdom } from '../services/firewallService';
import { FirewallVorlage } from '../services/firewallService';
import { extractApiError } from '../utils/errorUtils';
import {
  appendSnippet,
  COMMENT_REQUIRED_HINT,
  FirewallFarbe,
  findFlatIndex,
  firstOpenRuleIndex,
  firstWithoutConsensus,
  FlatVdom,
  flattenGeraete,
  matchesFarbe,
  nextRuleId,
  nextUnratedId,
  parseFarbeParam,
  ratingBlocked,
  RuleFilters,
  vdomParam,
  FARBE_FILL,
  FARBE_LABEL,
} from '../utils/firewallReview';
import FirewallRuleCard from '../components/firewall/FirewallRuleCard';
import FirewallRuleDrawer from '../components/firewall/FirewallRuleDrawer';
import { FinalRatingPanel, SwipeRatingPanel } from '../components/firewall/FirewallRatingPanel';
import { useSwipePalette } from '../components/firewall/firewallSwipeTheme';

interface FirewallReviewPageProps {
  mode: 'swipe' | 'final';
}

const ABSCHNITT_LABEL: Record<string, string> = {
  '0 byte rules': '0-Byte-Regeln',
  'Rules without a comment': 'Ohne Kommentar',
  'Other rules': 'Übrige Regeln',
};

function abschnittLabel(abschnitt: string): string {
  return ABSCHNITT_LABEL[abschnitt] ?? abschnitt;
}

function isOpen(rule: FirewallRegel, mode: 'swipe' | 'final'): boolean {
  return mode === 'swipe' ? !rule.eigene_bewertung : !rule.konsens;
}

type Flat = FlatVdom<FirewallGeraet, FirewallVdom>;

function doneCount(block: Pick<FirewallVdom, 'regeln' | 'bewertet'>, mode: 'swipe' | 'final'): number {
  return mode === 'final' ? block.regeln.filter((entry) => entry.konsens).length : block.bewertet;
}

function totalCount(block: Pick<FirewallVdom, 'regeln' | 'gesamt'>, mode: 'swipe' | 'final'): number {
  return mode === 'final' ? block.regeln.length : block.gesamt;
}

const FirewallReviewPage: React.FC<FirewallReviewPageProps> = ({ mode }) => {
  const { id } = useParams<{ id: string }>();
  const auditId = Number(id);
  const navigate = useNavigate();
  const palette = useSwipePalette();
  const [searchParams, setSearchParams] = useSearchParams();
  const [overview, setOverview] = useState<FirewallOverview | null>(null);
  const [vorlagen, setVorlagen] = useState<FirewallVorlage[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState(0);
  const [currentRuleId, setCurrentRuleId] = useState<number | null>(null);
  const [sectionFilter, setSectionFilter] = useState<string>('alle');
  const [colorFilter, setColorFilter] = useState<RuleFilters['farbe']>(() => parseFarbeParam(searchParams.get('farbe')));
  const [colorMenu, setColorMenu] = useState<HTMLElement | null>(null);
  const [text, setText] = useState('');
  const [chosen, setChosen] = useState<FirewallFarbe | null>(null);
  const [hint, setHint] = useState<string | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [vdomMenu, setVdomMenu] = useState<HTMLElement | null>(null);
  const [firewallMenu, setFirewallMenu] = useState<HTMLElement | null>(null);
  const [filterMenu, setFilterMenu] = useState<HTMLElement | null>(null);
  const textRef = useRef(text);
  textRef.current = text;
  const commentRef = useRef<HTMLTextAreaElement | null>(null);
  const jumpedRef = useRef(false);
  // Der erste Sprung setzt die Regel selbst. Der Fallback im selben Durchlauf würde sie sonst überschreiben.
  const skipFallbackRef = useRef(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [data, snippets] = await Promise.all([
        firewallService.getOverview(auditId, mode),
        firewallService.listVorlagen(false),
      ]);
      setOverview(data);
      setVorlagen(snippets);
    } catch (err) {
      setError(extractApiError(err));
    } finally {
      setLoading(false);
    }
  }, [auditId, mode]);

  useEffect(() => {
    void load();
  }, [load]);

  // Flache Liste „Firewall › VDOM“; `tab` ist der Index darin.
  const flat = useMemo<Flat[]>(() => (overview ? flattenGeraete(overview.geraete) : []), [overview]);
  const current = flat[tab];
  const block = current?.block;
  const geraet = current?.geraet;
  const mehrereFirewalls = overview ? overview.geraete.length > 1 : false;
  const regeln = useMemo(() => block?.regeln ?? [], [block]);
  const sequence = useMemo(
    () => regeln.filter((item) => (
      (sectionFilter === 'alle' || item.abschnitt === sectionFilter) && matchesFarbe(item, colorFilter)
    )),
    [regeln, sectionFilter, colorFilter],
  );
  const rule: FirewallRegel | undefined = useMemo(
    () => regeln.find((item) => item.id === currentRuleId) ?? undefined,
    [regeln, currentRuleId],
  );
  const position = rule ? sequence.findIndex((item) => item.id === rule.id) : -1;

  const firstOpenIn = useCallback((list: FirewallRegel[]): FirewallRegel | undefined => {
    if (list.length === 0) return undefined;
    const index = mode === 'swipe' ? firstOpenRuleIndex(list) : firstWithoutConsensus(list);
    return list[index];
  }, [mode]);

  const jumpToRule = useCallback((ruleId: number, data?: FirewallOverview | null) => {
    const source = data ? flattenGeraete(data.geraete) : flat;
    for (let tabIndex = 0; tabIndex < source.length; tabIndex += 1) {
      const target = source[tabIndex].block.regeln.find((item) => item.id === ruleId);
      if (target) {
        setTab(tabIndex);
        setCurrentRuleId(target.id);
        if (sectionFilter !== 'alle' && target.abschnitt !== sectionFilter) setSectionFilter('alle');
        return true;
      }
    }
    return false;
  }, [flat, sectionFilter]);

  // `?regel=<id>` bzw. `?fw=&vdom=` aus der Audit-Detailseite: einmal beim ersten Laden anspringen.
  useEffect(() => {
    if (!overview || jumpedRef.current) return;
    jumpedRef.current = true;
    skipFallbackRef.current = true;
    const source = flattenGeraete(overview.geraete);
    const farbe = parseFarbeParam(searchParams.get('farbe'));
    setColorFilter(farbe);
    const target = Number(searchParams.get('regel'));
    if (target && jumpToRule(target, overview)) {
      searchParams.delete('regel');
      setSearchParams(searchParams, { replace: true });
      return;
    }
    const pick = (rules: FirewallRegel[]) => {
      const list = rules.filter((item) => matchesFarbe(item, farbe));
      return firstOpenIn(list)?.id ?? list[0]?.id ?? null;
    };
    const wanted = findFlatIndex(source, searchParams.get('fw'), searchParams.get('vdom'));
    if (wanted >= 0) {
      setTab(wanted);
      setCurrentRuleId(pick(source[wanted]?.block.regeln ?? []));
      return;
    }
    if (farbe === 'alle') {
      setTab(0);
      setCurrentRuleId(firstOpenIn(source[0]?.block.regeln ?? [])?.id ?? null);
      return;
    }
    let chosenTab = 0;
    let chosenId: number | null = null;
    for (let index = 0; index < source.length; index += 1) {
      const list = (source[index]?.block.regeln ?? []).filter((item) => matchesFarbe(item, farbe));
      const openIndex = list.findIndex((item) => isOpen(item, mode));
      if (openIndex >= 0) {
        chosenTab = index;
        chosenId = list[openIndex].id;
        break;
      }
      if (chosenId == null && list[0]) {
        chosenTab = index;
        chosenId = list[0].id;
      }
    }
    setTab(chosenTab);
    setCurrentRuleId(chosenId);
  }, [overview, searchParams, setSearchParams, jumpToRule, firstOpenIn, mode]);

  // Gewählte Firewall und VDOM in der URL halten, damit Links und Reload direkt landen.
  useEffect(() => {
    if (!current || !jumpedRef.current) return;
    const fw = String(current.geraet.id);
    const vdom = vdomParam(current.block.vdom);
    if (searchParams.get('fw') === fw && searchParams.get('vdom') === vdom) return;
    const next = new URLSearchParams(searchParams);
    next.set('fw', fw);
    next.set('vdom', vdom);
    setSearchParams(next, { replace: true });
  }, [current, searchParams, setSearchParams]);

  // Tab außerhalb der Liste (z. B. VDOM verschoben): auf den ersten zurückfallen.
  useEffect(() => {
    if (!overview || loading) return;
    if (flat.length > 0 && tab >= flat.length) setTab(0);
  }, [overview, loading, flat, tab]);

  // Regel verschwunden (z. B. Upload gelöscht): auf die erste offene zurückfallen.
  // Solange `?regel=` noch in der URL steht, übernimmt der Sprung oben die Auswahl.
  useEffect(() => {
    if (!overview || loading || searchParams.get('regel')) return;
    if (skipFallbackRef.current) {
      skipFallbackRef.current = false;
      return;
    }
    if (currentRuleId != null && regeln.some((item) => item.id === currentRuleId)) return;
    setCurrentRuleId(firstOpenIn(sequence)?.id ?? sequence[0]?.id ?? null);
  }, [overview, loading, regeln, sequence, currentRuleId, firstOpenIn, searchParams]);

  useEffect(() => {
    if (!rule) return;
    if (mode === 'final') {
      setText(rule.konsens?.text ?? '');
      setChosen(rule.konsens?.farbe ?? null);
    } else {
      setText(rule.eigene_bewertung?.text ?? '');
      setChosen(rule.eigene_bewertung?.farbe ?? null);
    }
    setError(null);
    setHint(null);
  }, [rule, mode]);

  const goToTab = (index: number) => {
    setTab(index);
    setSectionFilter('alle');
    const list = (flat[index]?.block.regeln ?? []).filter((item) => matchesFarbe(item, colorFilter));
    setCurrentRuleId(firstOpenIn(list)?.id ?? list[0]?.id ?? null);
  };

  /** Firewall wechseln: erster VDOM der Firewall, bevorzugt einer mit offenen Regeln. */
  const goToFirewall = (firewallId: number) => {
    const candidates = flat.map((entry, index) => ({ entry, index })).filter(({ entry }) => entry.geraet.id === firewallId);
    if (candidates.length === 0) return;
    const open = candidates.find(({ entry }) => entry.block.regeln.some((item) => isOpen(item, mode)));
    goToTab((open ?? candidates[0]).index);
  };

  const visibleList = (abschnitt: string, farbe: RuleFilters['farbe']) => regeln.filter((item) => (
    (abschnitt === 'alle' || item.abschnitt === abschnitt) && matchesFarbe(item, farbe)
  ));

  const changeSection = (abschnitt: string) => {
    setSectionFilter(abschnitt);
    const list = visibleList(abschnitt, colorFilter);
    if (rule && list.some((item) => item.id === rule.id)) return;
    setCurrentRuleId(firstOpenIn(list)?.id ?? list[0]?.id ?? null);
  };

  const changeColor = (farbe: RuleFilters['farbe']) => {
    setColorFilter(farbe);
    const next = new URLSearchParams(searchParams);
    if (farbe === 'alle') next.delete('farbe');
    else next.set('farbe', farbe);
    setSearchParams(next, { replace: true });
    const list = visibleList(sectionFilter, farbe);
    if (rule && list.some((item) => item.id === rule.id)) return;
    setCurrentRuleId(firstOpenIn(list)?.id ?? list[0]?.id ?? null);
  };

  const neighbor = useCallback((direction: 1 | -1): FirewallRegel | undefined => {
    if (!rule) return undefined;
    if (position >= 0) return sequence[position + direction];
    // Aktuelle Regel liegt außerhalb des Filters: nächste Regel der Sequenz in Exportreihenfolge.
    const index = regeln.findIndex((item) => item.id === rule.id);
    const ordered = direction === 1
      ? sequence.find((item) => regeln.indexOf(item) > index)
      : [...sequence].reverse().find((item) => regeln.indexOf(item) < index);
    return ordered;
  }, [rule, position, sequence, regeln]);

  const prev = neighbor(-1);
  const next = neighbor(1);

  const unratedId = rule ? nextUnratedId(sequence, rule.id, mode) : null;

  const focusComment = useCallback((message: string) => {
    setHint(message);
    window.setTimeout(() => commentRef.current?.focus(), 0);
  }, []);

  const persist = useCallback(async (farbe: FirewallFarbe, comment: string) => {
    if (!rule) return;
    // Nächste Regel der Reihe vor dem Speichern, auch wenn die gespeicherte Regel danach die Farbe wechselt.
    const nextId = nextRuleId(sequence, rule.id) ?? rule.id;
    setChosen(farbe);
    if (ratingBlocked(farbe, comment)) {
      focusComment(COMMENT_REQUIRED_HINT);
      return;
    }
    setSaving(true);
    setError(null);
    setHint(null);
    try {
      if (mode === 'swipe') {
        await firewallService.saveBewertung(auditId, rule.id, farbe, comment);
      } else {
        await firewallService.saveKonsens(auditId, rule.id, farbe, comment);
      }
      const data = await firewallService.getOverview(auditId, mode);
      setOverview(data);
      setCurrentRuleId(nextId);
    } catch (err) {
      setError(extractApiError(err));
    } finally {
      setSaving(false);
    }
  }, [auditId, mode, rule, sequence, focusComment]);

  /** Auswahl einer Bewertungskarte: speichert noch nicht, erzwingt bei Rot/Gelb den Kommentar. */
  const selectRating = useCallback((farbe: FirewallFarbe) => {
    setChosen(farbe);
    setError(null);
    if (ratingBlocked(farbe, textRef.current)) {
      focusComment(COMMENT_REQUIRED_HINT);
    } else {
      setHint(null);
    }
  }, [focusComment]);

  const submit = useCallback(() => {
    if (!chosen) return;
    void persist(chosen, text);
  }, [chosen, text, persist]);

  const onTextChange = (value: string) => {
    setText(value);
    setError(null);
    if (value.trim().length > 0) setHint(null);
  };

  const insertSnippet = (snippet: string) => {
    setText((current) => appendSnippet(current, snippet));
    setError(null);
    setHint(null);
  };

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      const typing = target && (target.tagName === 'TEXTAREA' || target.tagName === 'INPUT' || target.isContentEditable);
      if (typing) return;
      if (event.key === 'l' || event.key === 'L') {
        if (event.ctrlKey || event.metaKey || event.altKey) return;
        event.preventDefault();
        setDrawerOpen((value) => !value);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const title = mode === 'swipe' ? 'Firewall-Bewertung' : 'Finaler Check';

  useEffect(() => {
    if (mode !== 'swipe') return undefined;
    const previous = document.title;
    document.title = title;
    return () => {
      document.title = previous;
    };
  }, [mode, title]);

  const done = block ? doneCount(block, mode) : 0;
  const total = block ? totalCount(block, mode) : 0;
  const percent = total > 0 ? Math.round((done / total) * 100) : 0;
  const gesamtDone = flat.reduce((sum, entry) => sum + doneCount(entry.block, mode), 0);
  const gesamtTotal = flat.reduce((sum, entry) => sum + totalCount(entry.block, mode), 0);
  const gesamtPercent = gesamtTotal > 0 ? Math.round((gesamtDone / gesamtTotal) * 100) : 0;
  const abschnitte = useMemo(() => Array.from(new Set(regeln.map((item) => item.abschnitt))), [regeln]);
  const sortedGeraete = useMemo(() => {
    const seen = new Map<number, FirewallGeraet>();
    flat.forEach((entry) => { if (!seen.has(entry.geraet.id)) seen.set(entry.geraet.id, entry.geraet); });
    return Array.from(seen.values());
  }, [flat]);
  const chipSx = { height: 24, fontWeight: 600, fontSize: '0.74rem', bgcolor: palette.tile, color: palette.text, border: `1px solid ${palette.tileBorder}` };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: palette.page, color: palette.text, display: 'flex', flexDirection: 'column' }}>
      {/* Kopfzeile */}
      <Box
        data-testid="firewall-topbar"
        sx={{ px: 2, py: 0.75, display: 'flex', alignItems: 'center', gap: 1.5, borderBottom: `1px solid ${palette.cardBorder}`, flexWrap: 'wrap' }}
      >
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(`/audits/${auditId}`)}
          sx={{ color: palette.green, fontWeight: 700, px: 1 }}
        >
          Audit
        </Button>
        <Typography component="h1" sx={{ fontWeight: 800, fontSize: '1.15rem' }}>{title}</Typography>
        {overview && flat.length > 0 && block && geraet && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }} data-testid="firewall-selection">
            {mehrereFirewalls && (
              <>
                <Tooltip title="Firewall wechseln" arrow>
                  <Chip
                    data-testid="firewall-geraet-chip"
                    size="small"
                    icon={<RouterIcon sx={{ fontSize: '14px !important', color: `${palette.muted} !important` }} />}
                    label={geraet.name}
                    onClick={(event) => setFirewallMenu(event.currentTarget)}
                    deleteIcon={<ExpandMoreIcon />}
                    onDelete={(event) => setFirewallMenu(event.currentTarget.closest('.MuiChip-root') as HTMLElement)}
                    sx={chipSx}
                  />
                </Tooltip>
                <Menu anchorEl={firewallMenu} open={Boolean(firewallMenu)} onClose={() => setFirewallMenu(null)}>
                  {sortedGeraete.map((item) => {
                    const itemDone = item.vdoms.reduce((sum, entry) => sum + doneCount(entry, mode), 0);
                    const itemTotal = item.vdoms.reduce((sum, entry) => sum + totalCount(entry, mode), 0);
                    return (
                      <MenuItem
                        key={item.id}
                        data-testid="firewall-geraet-tab"
                        selected={item.id === geraet.id}
                        onClick={() => { setFirewallMenu(null); goToFirewall(item.id); }}
                      >
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', width: '100%' }}>
                          <Typography sx={{ fontWeight: 600, flex: 1 }}>{item.name}</Typography>
                          <Typography variant="caption" color="text.secondary">{itemDone}/{itemTotal}</Typography>
                        </Box>
                      </MenuItem>
                    );
                  })}
                </Menu>
                <Typography sx={{ color: palette.muted, fontSize: '0.9rem', px: 0.25 }}>›</Typography>
              </>
            )}
            <Tooltip title="VDOM wechseln" arrow>
              <Chip
                data-testid="firewall-vdom-chip"
                size="small"
                label={block.label}
                onClick={(event) => setVdomMenu(event.currentTarget)}
                deleteIcon={<ExpandMoreIcon />}
                onDelete={(event) => setVdomMenu(event.currentTarget.closest('.MuiChip-root') as HTMLElement)}
                sx={chipSx}
              />
            </Tooltip>
            <Menu anchorEl={vdomMenu} open={Boolean(vdomMenu)} onClose={() => setVdomMenu(null)}>
              {flat.map((item, index) => {
                const itemDone = doneCount(item.block, mode);
                const itemTotal = totalCount(item.block, mode);
                const firstOfFirewall = index === 0 || flat[index - 1].geraet.id !== item.geraet.id;
                return (
                  <MenuItem
                    key={`${item.geraet.id}:${vdomParam(item.block.vdom)}`}
                    data-testid="firewall-vdom-tab"
                    selected={index === tab}
                    onClick={() => { setVdomMenu(null); goToTab(index); }}
                    sx={{ borderTop: mehrereFirewalls && firstOfFirewall && index > 0 ? '1px solid' : 'none', borderColor: 'divider' }}
                  >
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', width: '100%' }}>
                      <Typography sx={{ fontWeight: 600, flex: 1 }}>
                        {mehrereFirewalls && <Typography component="span" sx={{ color: 'text.secondary', fontWeight: 500 }}>{item.geraet.name} › </Typography>}
                        {item.block.label}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">{itemDone}/{itemTotal}</Typography>
                    </Box>
                  </MenuItem>
                );
              })}
            </Menu>
          </Box>
        )}
        <Box sx={{ flex: 1 }} />
        {block && (
          <Tooltip
            arrow
            title={mehrereFirewalls || flat.length > 1 ? `Gesamt über alle Firewalls: ${gesamtDone} von ${gesamtTotal} bewertet (${gesamtPercent} %)` : ''}
          >
            <Box data-testid="firewall-progress" sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
              <Typography sx={{ fontSize: '0.78rem', color: palette.muted }}>
                {done} von {total} bewertet
                {(mehrereFirewalls || flat.length > 1) && (
                  <Typography component="span" data-testid="firewall-progress-total" sx={{ fontSize: '0.78rem', color: palette.faint }}>
                    {' '}· gesamt {gesamtDone}/{gesamtTotal}
                  </Typography>
                )}
              </Typography>
              <LinearProgress
                variant="determinate"
                value={percent}
                sx={{ width: 150, height: 6, borderRadius: 3, bgcolor: palette.track, '& .MuiLinearProgress-bar': { bgcolor: palette.green, borderRadius: 3 } }}
              />
              <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, minWidth: 36, textAlign: 'right' }}>{percent} %</Typography>
            </Box>
          </Tooltip>
        )}
        <Tooltip title="Alle Regeln (Taste L)" arrow>
          <span>
            <IconButton
              data-testid="firewall-drawer-toggle"
              size="small"
              onClick={() => setDrawerOpen(true)}
              disabled={!overview || flat.length === 0}
              sx={{ color: palette.text, border: `1px solid ${palette.tileBorder}`, borderRadius: 1.5 }}
            >
              <FormatListBulletedIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {/* Navigation */}
      {block && flat.length > 0 && (
        <Box data-testid="firewall-pager" sx={{ px: 2, py: 1.25, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <IconButton
            data-testid="firewall-prev"
            size="small"
            disabled={!prev}
            onClick={() => prev && setCurrentRuleId(prev.id)}
            aria-label="Vorherige Regel"
            sx={{ border: `1px solid ${palette.tileBorder}`, borderRadius: 1.5, color: palette.text }}
          >
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
          <Typography data-testid="firewall-position" sx={{ fontSize: '0.82rem', px: 0.5, minWidth: 120, textAlign: 'center' }}>
            {sequence.length === 0
              ? 'Regel 0 von 0'
              : position >= 0
                ? `Regel ${position + 1} von ${sequence.length}`
                : `Außerhalb des Filters · ${sequence.length} Regeln`}
          </Typography>
          <IconButton
            data-testid="firewall-next"
            size="small"
            disabled={!next}
            onClick={() => next && setCurrentRuleId(next.id)}
            aria-label="Nächste Regel"
            sx={{ border: `1px solid ${palette.tileBorder}`, borderRadius: 1.5, color: palette.text }}
          >
            <ChevronRightIcon fontSize="small" />
          </IconButton>
          <Button
            data-testid="firewall-next-unrated"
            size="small"
            variant="outlined"
            disabled={unratedId == null}
            onClick={() => { if (unratedId != null) setCurrentRuleId(unratedId); }}
            sx={{ ml: 1, color: palette.text, borderColor: palette.tileBorder, fontSize: '0.78rem' }}
          >
            Nächste unbewertete
          </Button>
          <Button
            data-testid="firewall-section-filter"
            size="small"
            variant="outlined"
            onClick={(event) => setFilterMenu(event.currentTarget)}
            startIcon={<FilterListIcon sx={{ fontSize: '16px !important' }} />}
            endIcon={<ExpandMoreIcon sx={{ fontSize: '16px !important' }} />}
            sx={{ ml: 1, color: palette.text, borderColor: palette.tileBorder, fontSize: '0.78rem' }}
          >
            {sectionFilter === 'alle' ? 'Alle Abschnitte' : abschnittLabel(sectionFilter)}
          </Button>
          <Button
            data-testid="firewall-color-filter"
            data-farbe={colorFilter}
            size="small"
            variant="outlined"
            onClick={(event) => setColorMenu(event.currentTarget)}
            startIcon={colorFilter !== 'alle' && colorFilter !== 'offen' ? (
              <Box component="span" sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: FARBE_FILL[colorFilter] }} />
            ) : <FilterListIcon sx={{ fontSize: '16px !important' }} />}
            endIcon={<ExpandMoreIcon sx={{ fontSize: '16px !important' }} />}
            sx={{ color: palette.text, borderColor: palette.tileBorder, fontSize: '0.78rem' }}
          >
            {colorFilter === 'alle' ? 'Alle Farben' : colorFilter === 'offen' ? 'Offen' : FARBE_LABEL[colorFilter]}
          </Button>
          <Menu anchorEl={filterMenu} open={Boolean(filterMenu)} onClose={() => setFilterMenu(null)}>
            <MenuItem selected={sectionFilter === 'alle'} onClick={() => { setFilterMenu(null); changeSection('alle'); }}>
              Alle Abschnitte
            </MenuItem>
            {abschnitte.map((abschnitt) => (
              <MenuItem
                key={abschnitt}
                data-testid="firewall-section-option"
                selected={sectionFilter === abschnitt}
                onClick={() => { setFilterMenu(null); changeSection(abschnitt); }}
              >
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', width: '100%' }}>
                  <Typography sx={{ flex: 1 }}>{abschnittLabel(abschnitt)}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {regeln.filter((item) => item.abschnitt === abschnitt).length}
                  </Typography>
                </Box>
              </MenuItem>
            ))}
          </Menu>
          <Menu anchorEl={colorMenu} open={Boolean(colorMenu)} onClose={() => setColorMenu(null)}>
            <MenuItem
              data-testid="firewall-color-option"
              data-farbe="alle"
              selected={colorFilter === 'alle'}
              onClick={() => { setColorMenu(null); changeColor('alle'); }}
            >
              Alle Farben
            </MenuItem>
            {(['gruen', 'gelb', 'rot'] as const).map((farbe) => (
              <MenuItem
                key={farbe}
                data-testid="firewall-color-option"
                data-farbe={farbe}
                selected={colorFilter === farbe}
                onClick={() => { setColorMenu(null); changeColor(farbe); }}
              >
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', width: '100%' }}>
                  <Box component="span" sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: FARBE_FILL[farbe], flexShrink: 0 }} />
                  <Typography sx={{ flex: 1 }}>{FARBE_LABEL[farbe]}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {regeln.filter((item) => (sectionFilter === 'alle' || item.abschnitt === sectionFilter) && matchesFarbe(item, farbe)).length}
                  </Typography>
                </Box>
              </MenuItem>
            ))}
            <MenuItem
              data-testid="firewall-color-option"
              data-farbe="offen"
              selected={colorFilter === 'offen'}
              onClick={() => { setColorMenu(null); changeColor('offen'); }}
            >
              Offen
            </MenuItem>
          </Menu>
        </Box>
      )}

      <Box sx={{ flex: 1, px: 2, pb: 3 }}>
        {loading && <Box sx={{ display: 'flex', justifyContent: 'center', pt: 6 }}><CircularProgress /></Box>}
        {!loading && error && !rule && <Alert severity="error">{error}</Alert>}
        {!loading && overview && flat.length === 0 && (
          <Alert severity="info">Keine Regeln für {mode === 'swipe' ? 'die Bewertung' : 'den Finalen Check'}.</Alert>
        )}
        {!loading && overview && flat.length > 0 && !rule && !error && (
          <Alert severity="info" data-testid="firewall-filter-empty">
            {colorFilter === 'alle'
              ? `Keine Regeln für ${mode === 'swipe' ? 'die Bewertung' : 'den Finalen Check'}.`
              : 'Keine Regeln für diesen Farbfilter.'}
          </Alert>
        )}
        {rule && overview && block && (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) 340px' },
              gap: 2,
              alignItems: 'start',
            }}
          >
            <FirewallRuleCard
              rule={rule}
              vdomLabel={block.label}
              firewallName={mehrereFirewalls ? geraet?.name : undefined}
              rules={regeln}
              statistik={overview.statistik}
              vdomStatistik={block.statistik}
              team={overview.team}
              ratingMode={mode}
              onJumpToRule={(ruleId) => { jumpToRule(ruleId); }}
            />
            {mode === 'swipe' ? (
              <SwipeRatingPanel
                chosen={chosen}
                text={text}
                hint={hint}
                error={error}
                saving={saving}
                vorlagen={vorlagen}
                sprache={overview.sprache}
                commentRef={commentRef}
                onSelect={selectRating}
                onTextChange={onTextChange}
                onInsertSnippet={insertSnippet}
                onSubmit={submit}
              />
            ) : (
              <FinalRatingPanel
                rule={rule}
                team={overview.team}
                chosen={chosen}
                text={text}
                hint={hint}
                error={error}
                saving={saving}
                vorlagen={vorlagen}
                sprache={overview.sprache}
                commentRef={commentRef}
                onSelect={selectRating}
                onTextChange={onTextChange}
                onInsertSnippet={insertSnippet}
                onAdopt={(farbe, adopted) => {
                  setText(adopted);
                  setError(null);
                  setHint(null);
                  if (!chosen) setChosen(farbe);
                }}
                onSubmit={submit}
              />
            )}
          </Box>
        )}
      </Box>

      {overview && (
        <FirewallRuleDrawer
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          flat={flat}
          currentIndex={tab}
          currentRuleId={rule?.id ?? null}
          mode={mode}
          onSelect={(flatIndex, ruleId) => {
            setDrawerOpen(false);
            if (flatIndex !== tab) setTab(flatIndex);
            const target = flat[flatIndex]?.block.regeln.find((item) => item.id === ruleId);
            if (target && sectionFilter !== 'alle' && target.abschnitt !== sectionFilter) setSectionFilter('alle');
            setCurrentRuleId(ruleId);
          }}
        />
      )}
    </Box>
  );
};

export default FirewallReviewPage;
